// ─── ManagementDashboard ─────────────────────────────────────────────────────
// QC → Management Dashboard (/qc/management).
// How many customer accounts there are, day by day, over the last 2 months.
//
// The numbers come from GET /api/qc/management/accounts-daily, which counts
// the LoginCredentials table by Singapore day. Each point on the chart is
// the total number of accounts at the END of that day.
//
// Layout, top to bottom:
//   • one big number (total accounts today) and two small ones (new this
//     week, new in the 2 months shown);
//   • the chart: one green line with a light green wash under it. Point at
//     it (or tap it) to see that day's numbers. There's a "Table" view with
//     every day's numbers, for anyone who'd rather read than look.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot,
} from 'recharts';

import { fetchAccountsDaily } from '../../api/qcApi';
import { friendlyMessage } from '../../../api/apiClient';
import { LoadingState, ErrorState } from '../../../shared/components/status';
import {
  GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50, labelMono, bodyFont,
} from '../../../theme/tokens2';

// Chart chrome: quiet greys so the data line is the only loud thing.
const GRID = '#ececec';
const AXIS = '#d9d9d9';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// '2026-10-19' → a Date at local midnight (not UTC, which can shift the day).
const toDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const shortDate = (iso) => toDate(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
const longDate  = (iso) => toDate(iso).toLocaleDateString('en-SG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

const whole   = new Intl.NumberFormat('en-SG');
const compact = new Intl.NumberFormat('en-SG', { notation: 'compact', maximumFractionDigits: 1 });
const plural  = (n, one, many = `${one}s`) => `${whole.format(n)} ${n === 1 ? one : many}`;

// ─── StatTile ────────────────────────────────────────────────────────────────
// hero = the one big number on the page.
function StatTile({ label, value, note, hero = false }) {
  return (
    <Box sx={{
      flex: hero ? '1.4 1 220px' : '1 1 160px',
      border: `1px solid ${BORDER}`, background: '#fff', p: '16px 18px',
    }}>
      <Typography sx={{ fontSize: '12px', color: MUTED, ...bodyFont }}>{label}</Typography>
      <Typography sx={{
        fontSize: hero ? '48px' : '28px', fontWeight: 600, lineHeight: 1.1,
        letterSpacing: '-0.02em', color: CHARCOAL, mt: '6px', ...bodyFont,
      }}>
        {value}
      </Typography>
      {note && (
        <Typography sx={{ fontSize: '12px', color: MUTED, mt: '6px', ...bodyFont }}>{note}</Typography>
      )}
    </Box>
  );
}

// ─── Chart tooltip ───────────────────────────────────────────────────────────
function DayTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const day = payload[0].payload;
  return (
    <Box sx={{
      background: '#fff', border: `1px solid ${BORDER}`, boxShadow: '0 4px 14px rgba(0,0,0,0.10)',
      px: '12px', py: '9px', minWidth: 150,
    }}>
      <Typography sx={{ fontSize: '11px', color: MUTED, ...bodyFont }}>{longDate(day.date)}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mt: '5px' }}>
        <Box aria-hidden sx={{ width: 10, height: 2, background: GREEN, borderRadius: '1px' }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: CHARCOAL, ...bodyFont }}>
          {plural(day.totalAccounts, 'account')}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '11px', color: MUTED, mt: '2px', ...bodyFont }}>
        {day.newAccounts > 0 ? `+${whole.format(day.newAccounts)} new that day` : 'No new accounts that day'}
      </Typography>
    </Box>
  );
}

// ─── Chart ───────────────────────────────────────────────────────────────────
function AccountsChart({ days }) {
  const last = days[days.length - 1];
  return (
    <Box
      role="img"
      aria-label={`Line chart of total customer accounts per day, from ${longDate(days[0].date)} (${whole.format(days[0].totalAccounts)}) to ${longDate(last.date)} (${whole.format(last.totalAccounts)}). Switch to Table for every day's numbers.`}
      sx={{ width: '100%', height: 320, '& text': { fontFamily: bodyFont.fontFamily } }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={days} margin={{ top: 28, right: 28, bottom: 4, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            minTickGap={28}
            tickLine={false}
            axisLine={{ stroke: AXIS }}
            tick={{ fill: MUTED, fontSize: 11 }}
            tickMargin={8}
          />
          <YAxis
            allowDecimals={false}
            domain={[0, 'auto']}
            tickFormatter={(v) => compact.format(v)}
            tickLine={false}
            axisLine={false}
            tick={{ fill: MUTED, fontSize: 11 }}
            width={44}
          />
          <Tooltip
            content={<DayTooltip />}
            cursor={{ stroke: '#bdbdbd', strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Area
            type="linear"
            dataKey="totalAccounts"
            name="Total accounts"
            stroke={GREEN}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            fill={GREEN}
            fillOpacity={0.1}
            dot={false}
            activeDot={{ r: 5, fill: GREEN, stroke: '#fff', strokeWidth: 2 }}
            isAnimationActive={false}
          />
          {/* Today's value, labelled at the end of the line */}
          <ReferenceDot
            x={last.date}
            y={last.totalAccounts}
            r={4}
            fill={GREEN}
            stroke="#fff"
            strokeWidth={2}
            label={{ value: whole.format(last.totalAccounts), position: 'top', offset: 10, fill: CHARCOAL, fontSize: 12, fontWeight: 600 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ─── Table view ──────────────────────────────────────────────────────────────
function AccountsTable({ days }) {
  const rows = [...days].reverse(); // newest first
  const cell = { px: '14px', py: '8px', fontSize: '13px', borderBottom: `1px solid ${GRID}`, ...bodyFont };
  const num  = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  return (
    <Box sx={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${BORDER}` }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <Box component="caption" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Total customer accounts per day, newest first
        </Box>
        <Box component="thead" sx={{ position: 'sticky', top: 0, background: STONE50 }}>
          <tr>
            <Box component="th" scope="col" sx={{ ...cell, textAlign: 'left', fontWeight: 600, color: MUTED }}>Date</Box>
            <Box component="th" scope="col" sx={{ ...num, fontWeight: 600, color: MUTED }}>New that day</Box>
            <Box component="th" scope="col" sx={{ ...num, fontWeight: 600, color: MUTED }}>Total accounts</Box>
          </tr>
        </Box>
        <tbody>
          {rows.map((d) => (
            <tr key={d.date}>
              <Box component="td" sx={{ ...cell, color: CHARCOAL }}>{longDate(d.date)}</Box>
              <Box component="td" sx={{ ...num, color: d.newAccounts ? CHARCOAL : MUTED_FAINT }}>
                {d.newAccounts ? `+${whole.format(d.newAccounts)}` : '0'}
              </Box>
              <Box component="td" sx={{ ...num, color: CHARCOAL, fontWeight: 500 }}>{whole.format(d.totalAccounts)}</Box>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

// ─── View switch (Chart | Table) ─────────────────────────────────────────────
function ViewSwitch({ view, onChange }) {
  return (
    <Box role="group" aria-label="Show as" sx={{ display: 'flex', border: `1px solid ${BORDER}` }}>
      {['chart', 'table'].map((v) => (
        <Box
          key={v}
          component="button"
          type="button"
          aria-pressed={view === v}
          onClick={() => onChange(v)}
          sx={{
            ...labelMono, fontSize: '9px', px: '12px', minHeight: 32,
            border: 'none', cursor: 'pointer',
            background: view === v ? CHARCOAL : '#fff',
            color: view === v ? '#fff' : MUTED,
            '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
          }}
        >
          {v === 'chart' ? 'Chart' : 'Table'}
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function ManagementDashboard() {
  const navigate = useNavigate();
  const [data,      setData]      = useState(null);
  const [status,    setStatus]    = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError, setLoadError] = useState('');
  const [view,      setView]      = useState('chart');
  const [loadedAt,  setLoadedAt]  = useState(null);

  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading')); // a refresh keeps the chart on screen
    try {
      setData(await fetchAccountsDaily());
      setLoadedAt(new Date());
      setStatus('ready');
    } catch (err) {
      setLoadError(friendlyMessage(err, "We couldn't load the numbers. Please try again."));
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const days = data?.days || [];
    if (!days.length) return null;
    const total    = days[days.length - 1].totalAccounts;
    const lastWeek = days.slice(-7).reduce((sum, d) => sum + d.newAccounts, 0);
    const period   = days.reduce((sum, d) => sum + d.newAccounts, 0);
    return { total, lastWeek, period };
  }, [data]);

  return (
    <Box sx={{ minHeight: '100vh', background: STONE50 }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: '16px', md: '32px' }, py: { xs: '24px', md: '36px' } }}>

        {/* ── Header ── */}
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/qc')}
          sx={{
            background: 'none', border: 'none', p: 0, cursor: 'pointer',
            fontSize: '13px', color: MUTED, ...bodyFont,
            '&:hover': { color: CHARCOAL, textDecoration: 'underline' },
          }}
        >
          ← Quality Control
        </Box>
        <Typography component="h1" sx={{ fontSize: '26px', fontWeight: 600, color: CHARCOAL, mt: '10px', ...bodyFont }}>
          Management Dashboard
        </Typography>
        <Typography sx={{ fontSize: '14px', color: MUTED, mt: '4px', mb: '24px', ...bodyFont }}>
          Customer accounts over the last 2 months. Days run midnight to midnight, Singapore time.
        </Typography>

        {status === 'loading' && <LoadingState label="Loading the numbers…" size="page" />}

        {status === 'error' && (
          <ErrorState title="We couldn't load the dashboard" message={loadError} onRetry={load} />
        )}

        {status === 'ready' && stats && (
          <>
            {/* ── Numbers ── */}
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: '16px' }}>
              <StatTile hero label="Total accounts" value={whole.format(stats.total)}
                note={`As of today, ${shortDate(data.to)}`} />
              <StatTile label="New in the last 7 days" value={`+${whole.format(stats.lastWeek)}`} />
              <StatTile label="New in the last 2 months" value={`+${whole.format(stats.period)}`}
                note={`Since ${shortDate(data.from)}`} />
            </Box>

            {/* ── Chart / table ── */}
            <Box sx={{ border: `1px solid ${BORDER}`, background: '#fff', p: { xs: '14px', md: '20px' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: '12px', flexWrap: 'wrap' }}>
                <Box>
                  <Typography component="h2" sx={{ fontSize: '15px', fontWeight: 600, color: CHARCOAL, ...bodyFont }}>
                    Total accounts per day
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: MUTED, mt: '2px', ...bodyFont }}>
                    {shortDate(data.from)} – {shortDate(data.to)} · each point is the total at the end of that day
                  </Typography>
                </Box>
                <ViewSwitch view={view} onChange={setView} />
              </Box>

              {view === 'chart' ? <AccountsChart days={data.days} /> : <AccountsTable days={data.days} />}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: '10px', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '12px', color: MUTED_FAINT, ...bodyFont }}>
                Counts the accounts that exist now. A deleted account doesn't appear on any day.
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={load}
                sx={{
                  background: 'none', border: 'none', p: 0, cursor: 'pointer',
                  fontSize: '12px', color: MUTED, ...bodyFont,
                  '&:hover': { color: CHARCOAL, textDecoration: 'underline' },
                }}
              >
                Updated {loadedAt?.toLocaleTimeString('en-SG', { hour: 'numeric', minute: '2-digit' })} · Refresh
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
